/**
 * Service to handle all secret related tasks
 */

import DOMPurify from "dompurify";
import i18n from "../i18n";
import apiClient from "../services/api-client";
import browserClient from "../services/browser-client";
import connectionCredentialsService from "../services/connection-credentials";
import cryptoLibrary from "../services/crypto-library";
import offlineCache from "../services/offline-cache";
import deviceService from "./device";
import notification from "./notification";
import { getPasskeyUrl } from "./passkey-url";
import storage from "./storage";
import { getStore } from "./store";
import type {
	BulkSecretInput,
	CreatedSecret,
	EncryptedSecret,
	SecretContent,
	SecretMetadata,
	SecretReference,
} from "../../types/vault";

// Keep these fields and the encrypted JSON timestamp format in sync with psono-app.
const passwordFields = [
	"website_password_password",
	"application_password_password",
	"elster_certificate_password",
	"ssh_connection_password",
	"rdp_connection_password",
	"vnc_connection_password",
] as const;

function hasPasswordContent(content: SecretContent): boolean {
	return passwordFields.some(
		(field) =>
			Object.hasOwn(content, field) ||
			Object.hasOwn(content, field.replace(/_password$/, "_title")),
	);
}

function validPasswordModificationDatetime(value: unknown): string | undefined {
	return typeof value === "string" && Number.isFinite(Date.parse(value))
		? value
		: undefined;
}

/** Update only when the password changes, preserving the age of legacy entries. */
function updatePasswordModificationDatetime(
	content: SecretContent,
	previous?: SecretContent & SecretMetadata,
): void {
	if (
		!passwordFields.some(
			(field) =>
				Object.hasOwn(content, field) ||
				(previous && Object.hasOwn(previous, field)),
		)
	) {
		return;
	}
	if (
		previous &&
		passwordFields.some(
			(field) => (content[field] || "") !== (previous[field] || ""),
		)
	) {
		content.password_modification_datetime = new Date().toISOString();
		return;
	}
	const timestamp = previous
		? validPasswordModificationDatetime(
				previous.password_modification_datetime,
			) || validPasswordModificationDatetime(previous.write_date)
		: validPasswordModificationDatetime(
				content.password_modification_datetime,
			) || new Date().toISOString();
	if (timestamp) {
		content.password_modification_datetime = timestamp;
	} else {
		delete content.password_modification_datetime;
	}
}

/**
 * Bulk write of secrets. Encrypts the content and creates a new secret out of it.
 *
 * @param {array} objects The content of the new secret
 * @param {uuid|undefined} [parentDatastoreId] (optional) The id of the parent datastore, may be left empty if the share resides in a share
 * @param {uuid|undefined} [parentShareId] (optional) The id of the parent share, may be left empty if the share resides in the datastore
 *
 * @returns {Promise} Returns a promise with a list of dictionaries with the new secret_id and provided link_ids
 */
function createSecretBulk(
	objects: BulkSecretInput[],
	parentDatastoreId?: string,
	parentShareId?: string,
) {
	const token = getStore().getState().user.token;
	const sessionSecretKey = getStore().getState().user.sessionSecretKey;

	const encryptionKeyLookup: Record<string, string> = {};

	const bulkObjects = objects.map((o) => {
		const secretKey = cryptoLibrary.generateSecretKey();
		encryptionKeyLookup[o.linkId] = secretKey;
		updatePasswordModificationDatetime(o.content);
		const jsonContent = JSON.stringify(o.content);

		const c = cryptoLibrary.encryptData(jsonContent, secretKey);

		return {
			data: c.text,
			data_nonce: c.nonce,
			link_id: o.linkId,
			callback_url: o.callbackUrl,
			callback_user: o.callbackUser,
			callback_pass: o.callbackPass,
		};
	});

	const onError = (result: unknown) => Promise.reject(result);

	const onSuccess = (response: {
		data: { secrets: { secret_id: string; link_id: string }[] };
	}) =>
		response.data.secrets.map((s) => ({
			secret_id: s.secret_id,
			secret_key: encryptionKeyLookup[s.link_id],
			link_id: s.link_id,
		}));

	return apiClient
		.createSecretBulk(
			token,
			sessionSecretKey,
			bulkObjects,
			parentDatastoreId,
			parentShareId,
		)
		.then(onSuccess, onError);
}

/**
 * Bulk read of secrets
 *
 * @param {array} bulkObjects A list of tuples. The first one being the secretId, the second one the secretKey
 *
 * @returns {Promise} Returns a promise with a list of dictionaries with the new secret_id and provided link_ids
 */
function readSecretBulk(
	bulkObjects: ReadonlyArray<readonly [secretId: string, secretKey: string]>,
) {
	const token = getStore().getState().user.token;
	const sessionSecretKey = getStore().getState().user.sessionSecretKey;

	const encryptionKeyLookup: Record<string, string> = {};
	const secretIds: string[] = [];
	for (const o of bulkObjects) {
		secretIds.push(o[0]);
		encryptionKeyLookup[o[0]] = o[1];
	}

	const onError = (result: unknown) => Promise.reject(result);

	const onSuccess = (response: { data: { secrets: EncryptedSecret[] } }) =>
		response.data.secrets.map((content) => {
			const secret: SecretContent & SecretMetadata = JSON.parse(
				cryptoLibrary.decryptData(
					content.data,
					content.data_nonce,
					encryptionKeyLookup[content["id"]],
				),
			);
			secret["id"] = content["id"];
			secret["read_count"] = content["read_count"];
			secret["create_date"] = content["create_date"];
			secret["write_date"] = content["write_date"];
			secret["callback_url"] = content["callback_url"];
			secret["callback_user"] = content["callback_user"];
			secret["callback_pass"] = content["callback_pass"];
			return secret;
		});

	return apiClient
		.readSecretBulk(token, sessionSecretKey, secretIds)
		.then(onSuccess, onError);
}

/**
 * Encrypts the content and creates a new secret out of it.
 *
 * @param {object} content The content of the new secret
 * @param {uuid} linkId the local id of the share in the data structure
 * @param {uuid|undefined} [parentDatastoreId] (optional) The id of the parent datastore, may be left empty if the share resides in a share
 * @param {uuid|undefined} [parentShareId] (optional) The id of the parent share, may be left empty if the share resides in the datastore
 * @param {string} callbackUrl The callback ULR
 * @param {string} callbackUser The callback user
 * @param {string} callbackPass The callback password
 *
 * @returns {Promise} Returns a promise with the new secret_id
 */
function createSecret(
	content: SecretContent,
	linkId: string,
	parentDatastoreId?: string,
	parentShareId?: string,
	callbackUrl?: string,
	callbackUser?: string,
	callbackPass?: string,
) {
	const token = getStore().getState().user.token;
	const sessionSecretKey = getStore().getState().user.sessionSecretKey;
	const secretKey = cryptoLibrary.generateSecretKey();

	updatePasswordModificationDatetime(content);
	const jsonContent = JSON.stringify(content);

	const c = cryptoLibrary.encryptData(jsonContent, secretKey);

	const onError = () => {
		// pass
	};

	const onSuccess = (response: {
		data: { secret_id: string };
	}): CreatedSecret => ({
		secret_id: response.data.secret_id,
		secret_key: secretKey,
	});

	return apiClient
		.createSecret(
			token,
			sessionSecretKey,
			c.text,
			c.nonce,
			linkId,
			parentDatastoreId,
			parentShareId,
			callbackUrl,
			callbackUser,
			callbackPass,
		)
		.then(onSuccess, onError);
}

/**
 * Reads a secret and decrypts it. Returns the decrypted object
 *
 * @param {uuid} secretId The secret id one wants to fetch
 * @param {string} secretKey The secret key to decrypt the content
 *
 * @returns {Promise} Returns a promise withe decrypted content of the secret
 */
function readSecret<T extends SecretContent = SecretContent>(
	secretId: string,
	secretKey: string,
): Promise<T & SecretMetadata> {
	const token = getStore().getState().user.token;
	const sessionSecretKey = getStore().getState().user.sessionSecretKey;
	const onError = (result: unknown) => Promise.reject(result);

	const onSuccess = (content: { data: EncryptedSecret }) => {
		// The encrypted payload is versioned by the secret's own type, not the API envelope.
		const secret: T & SecretMetadata = JSON.parse(
			cryptoLibrary.decryptData(
				content.data.data,
				content.data.data_nonce,
				secretKey,
			),
		);
		if (content.data) {
			secret["read_count"] = content.data["read_count"];
		}
		secret["create_date"] = content.data["create_date"];
		secret["write_date"] = content.data["write_date"];
		secret["callback_url"] = content.data["callback_url"];
		secret["callback_user"] = content.data["callback_user"];
		secret["callback_pass"] = content.data["callback_pass"];
		return secret;
	};
	return apiClient
		.readSecret(token, sessionSecretKey, secretId)
		.then(onSuccess, onError);
}

/**
 * Encrypts some content and updates a secret with it. returns the secret id
 *
 * @param {uuid} secretId The id of the secret
 * @param {string} secretKey The secret key of the secret
 * @param {object} content The new content for the given secret
 * @param {string} callbackUrl The callback ULR
 * @param {string} callbackUser The callback user
 * @param {string} callbackPass The callback password
 *
 * @returns {Promise} Returns a promise with the secret id
 */
async function writeSecret(
	secretId: string,
	secretKey: string,
	content: SecretContent,
	callbackUrl?: string | null,
	callbackUser?: string | null,
	callbackPass?: string | null,
) {
	const token = getStore().getState().user.token;
	const sessionSecretKey = getStore().getState().user.sessionSecretKey;

	if (hasPasswordContent(content)) {
		const previous = await readSecret(secretId, secretKey);
		updatePasswordModificationDatetime(content, previous);
	}
	const jsonContent = JSON.stringify(content);

	const c = cryptoLibrary.encryptData(jsonContent, secretKey);

	const onError = (result: unknown) => {
		console.log(result);
	};

	const onSuccess = (response: { data: { secret_id: string } }) => ({
		secret_id: response.data.secret_id,
	});

	return apiClient
		.writeSecret(
			token,
			sessionSecretKey,
			secretId,
			c.text,
			c.nonce,
			callbackUrl,
			callbackUser,
			callbackPass,
		)
		.then(onSuccess, onError);
}

/**
 * Fetches and decrypts a secret and initiates the redirect for the secret
 *
 * @param {string} type The type of the secret
 * @param {uuid} secretId The id of the secret to read
 */
function redirectSecret(type: string, secretId: string) {
	return storage.findKey("datastore-password-leafs", secretId).then((leaf) => {
		if (leaf === null) {
			console.log("redirectSecret: leaf not found");
			return;
		}
		const onError = (result: unknown) => {
			// pass
			console.log(result);
		};

		const onSuccess = (content: SecretContent) => {
			if (type === "website_password") {
				browserClient.emitSec("fillpassword", {
					username: content.website_password_username,
					password: content.website_password_password,
					totp_token: content.website_password_totp_code
						? cryptoLibrary.getTotpToken(
								content.website_password_totp_code,
								content.website_password_totp_period,
								content.website_password_totp_algorithm,
								content.website_password_totp_digits,
							)
						: "",
					url_filter: content.website_password_url_filter,
					auto_submit: content.website_password_auto_submit,
					custom_fields: content.custom_fields || [],
				});

				let url = content.website_password_url;

				if (!url) {
					console.log("redirectSecret: URL_EMPTY_CANNOT_REDIRECT");
					return Promise.reject({
						non_field_errors: ["URL_EMPTY_CANNOT_REDIRECT"],
					});
				}

				if (!url.includes("://")) {
					url = "https://" + url;
				}
				if (!DOMPurify.isValidAttribute("a", "href", url)) {
					// sanitizes URL to avoid javascript: XSS
					url = "about:blank";
				}
				window.location.href = url;
			} else if (type === "bookmark" || type === "passkey") {
				let url =
					type === "passkey" ? getPasskeyUrl(content) : content.bookmark_url;

				if (!url) {
					console.log("redirectSecret: URL_EMPTY");
					return Promise.reject({
						non_field_errors: ["URL_EMPTY"],
					});
				}

				if (!url.includes("://")) {
					url = "https://" + url;
				}
				if (!DOMPurify.isValidAttribute("a", "href", url)) {
					// sanitizes URL to avoid javascript: XSS
					url = "about:blank";
				}
				window.location.href = url;
			} else if (type === "elster_certificate") {
				browserClient.emitSec("fillelstercertificate", {
					elster_certificate_title: content.elster_certificate_title,
					elster_certificate_file_content:
						content.elster_certificate_file_content,
					elster_certificate_password: content.elster_certificate_password,
				});
				window.location.href = "https://www.elster.de/eportal/login/softpse";
			} else {
				window.location.href = "index.html#!/datastore/search/" + secretId;
			}
		};

		return readSecret(secretId, leaf.secret_key).then(onSuccess, onError);
	});
}

/**
 * Handles item clicks and triggers redirects for website passwords and bookmarks
 *
 * @param {object} item The item one has clicked on
 */
function onItemClick(item: SecretReference) {
	if (
		["website_password", "bookmark", "passkey", "elster_certificate"].indexOf(
			item.type,
		) !== -1
	) {
		if (deviceService.isElectron()) {
			readSecret(item.secret_id, item.secret_key).then((content) => {
				if (item.type === "website_password") {
					browserClient.openTab(content.website_password_url!);
				} else if (item.type === "bookmark") {
					browserClient.openTab(content.bookmark_url!);
				} else if (item.type === "passkey") {
					browserClient.openTab(getPasskeyUrl(content));
				} else if (item.type === "elster_certificate") {
					browserClient.openTab("https://www.elster.de/eportal/login/softpse");
				}
			});
		} else {
			browserClient
				.openTab(
					"open-secret.html#!/secret/" + item.type + "/" + item.secret_id,
				)
				.then((window) => {
					window!.psono_offline_cache_encryption_key =
						offlineCache.getEncryptionKey();
				});
		}
	}
}

/**
 * Copies the username of a given secret to the clipboard
 *
 * @param {object} item The item of which we want to load the username into our clipboard
 */
function copyUsername(item: SecretReference) {
	let copyPromise;
	if (item["type"] === "application_password") {
		copyPromise = browserClient.copyToClipboard(() =>
			readSecret(item.secret_id, item.secret_key).then(
				(decryptedSecret) => decryptedSecret["application_password_username"]!,
			),
		);
	} else if (item["type"] === "website_password") {
		copyPromise = browserClient.copyToClipboard(() =>
			readSecret(item.secret_id, item.secret_key).then(
				(decryptedSecret) => decryptedSecret["website_password_username"]!,
			),
		);
	} else if (
		["ssh_connection", "rdp_connection", "vnc_connection"].includes(
			item["type"],
		)
	) {
		copyPromise = browserClient.copyToClipboard(() =>
			readSecret(item.secret_id, item.secret_key).then((connection) =>
				connectionCredentialsService
					.resolveConnectionAuthentication(
						item.type,
						item.secret_id,
						connection,
						readSecret,
					)
					.then((authentication) => authentication?.username || ""),
			),
		);
	}
	if (!copyPromise) {
		return;
	}
	return copyPromise.then(
		() =>
			notification.push("username_copy", i18n.t("USERNAME_COPY_NOTIFICATION")),
		() =>
			notification.push(
				"connection_authentication",
				i18n.t("BROKEN_REFERENCE"),
			),
	);
}

/**
 * Copies the password of a given secret to the clipboard
 *
 * @param {object} item The item of which we want to load the password into our clipboard
 */
function copyPassword(item: SecretReference) {
	let copyPromise;
	if (item["type"] === "application_password") {
		copyPromise = browserClient.copyToClipboard(() =>
			readSecret(item.secret_id, item.secret_key).then(
				(decryptedSecret) => decryptedSecret["application_password_password"]!,
			),
		);
	} else if (item["type"] === "website_password") {
		copyPromise = browserClient.copyToClipboard(() =>
			readSecret(item.secret_id, item.secret_key).then(
				(decryptedSecret) => decryptedSecret["website_password_password"]!,
			),
		);
	} else if (
		["ssh_connection", "rdp_connection", "vnc_connection"].includes(
			item["type"],
		)
	) {
		copyPromise = browserClient.copyToClipboard(() =>
			readSecret(item.secret_id, item.secret_key).then((connection) =>
				connectionCredentialsService
					.resolveConnectionAuthentication(
						item.type,
						item.secret_id,
						connection,
						readSecret,
					)
					.then((authentication) => authentication?.password || ""),
			),
		);
	}
	if (!copyPromise) {
		return;
	}
	return copyPromise.then(
		() =>
			notification.push("password_copy", i18n.t("PASSWORD_COPY_NOTIFICATION")),
		() =>
			notification.push(
				"connection_authentication",
				i18n.t("BROKEN_REFERENCE"),
			),
	);
}

/**
 * Copies the password of a given secret to the clipboard
 *
 * @param {object} item The item of which we want to load the password into our clipboard
 */
function copyUrl(item: SecretReference) {
	if (item["type"] === "website_password") {
		browserClient.copyToClipboard(() =>
			readSecret(item.secret_id, item.secret_key).then(
				(decryptedSecret) => decryptedSecret["website_password_url"]!,
			),
		);
	} else if (item["type"] === "passkey") {
		browserClient.copyToClipboard(() =>
			readSecret(item.secret_id, item.secret_key).then(getPasskeyUrl),
		);
	} else if (item["type"] === "bookmark") {
		browserClient.copyToClipboard(() =>
			readSecret(item.secret_id, item.secret_key).then(
				(decryptedSecret) => decryptedSecret["bookmark_url"]!,
			),
		);
	}

	notification.push("password_copy", i18n.t("URL_COPY_NOTIFICATION"));
}

/**
 * Copies the TOTP token of a given secret to the clipboard
 *
 * @param {object} item The item of which we want to load the TOTP token into our clipboard
 */
function copyTotpToken(item: SecretReference) {
	browserClient.copyToClipboard(() =>
		readSecret(item.secret_id, item.secret_key).then((decryptedSecret) => {
			let totpPeriod, totpAlgorithm, totpDigits, totpCode;
			if (item["type"] === "website_password") {
				totpCode = decryptedSecret["website_password_totp_code"];
				if (Object.hasOwn(decryptedSecret, "website_password_totp_period")) {
					totpPeriod = decryptedSecret["website_password_totp_period"];
				}
				if (Object.hasOwn(decryptedSecret, "website_password_totp_algorithm")) {
					totpAlgorithm = decryptedSecret["website_password_totp_algorithm"];
				}
				if (Object.hasOwn(decryptedSecret, "website_password_totp_digits")) {
					totpDigits = decryptedSecret["website_password_totp_digits"];
				}
			} else if (item["type"] === "totp") {
				totpCode = decryptedSecret["totp_code"];
				if (Object.hasOwn(decryptedSecret, "totp_period")) {
					totpPeriod = decryptedSecret["totp_period"];
				}
				if (Object.hasOwn(decryptedSecret, "totp_algorithm")) {
					totpAlgorithm = decryptedSecret["totp_algorithm"];
				}
				if (Object.hasOwn(decryptedSecret, "totp_digits")) {
					totpDigits = decryptedSecret["totp_digits"];
				}
			}
			if (!totpCode) {
				return "";
			}
			return cryptoLibrary.getTotpToken(
				totpCode,
				totpPeriod,
				totpAlgorithm,
				totpDigits,
			);
		}),
	);
	notification.push("totp_token_copy", i18n.t("TOTP_TOKEN_COPY_NOTIFICATION"));
}

/**
 * Copies the note content of a given secret to the clipboard
 *
 * @param {object} item The item of which we want to load the note content into our clipboard
 */
function copyNoteContent(item: SecretReference) {
	browserClient.copyToClipboard(() =>
		readSecret(item.secret_id, item.secret_key).then((decryptedSecret) =>
			decryptedSecret["note_notes"]!.replace(/\\n/g, "\n"),
		),
	);
	notification.push(
		"note_content_copy",
		i18n.t("NOTE_CONTENT_COPY_NOTIFICATION"),
	);
}

/**
 * Copies the credit card number of a given secret to the clipboard
 *
 * @param {object} item The item of which we want to load the credit card number into our clipboard
 */
function copyCreditCardNumber(item: SecretReference) {
	browserClient.copyToClipboard(() =>
		readSecret(item.secret_id, item.secret_key).then(
			(decryptedSecret) => decryptedSecret["credit_card_number"]!,
		),
	);
	notification.push(
		"credit_card_number_copy",
		i18n.t("CREDIT_CARD_NUMBER_COPY_NOTIFICATION"),
	);
}

/**
 * Copies the credit card name of a given secret to the clipboard
 *
 * @param {object} item The item of which we want to load the credit card name into our clipboard
 */
function copyCreditCardName(item: SecretReference) {
	browserClient.copyToClipboard(() =>
		readSecret(item.secret_id, item.secret_key).then(
			(decryptedSecret) => decryptedSecret["credit_card_name"]!,
		),
	);
	notification.push(
		"credit_card_name_copy",
		i18n.t("CREDIT_CARD_NAME_COPY_NOTIFICATION"),
	);
}

/**
 * Copies the credit card expiry date of a given secret to the clipboard
 *
 * @param {object} item The item of which we want to load the credit card expiry date into our clipboard
 */
function copyCreditCardExpiryDate(item: SecretReference) {
	browserClient.copyToClipboard(() =>
		readSecret(item.secret_id, item.secret_key).then(
			(decryptedSecret) => decryptedSecret["credit_card_valid_through"]!,
		),
	);
	notification.push(
		"credit_card_expiry_date_copy",
		i18n.t("CREDIT_CARD_EXPIRATION_DATE_COPY_NOTIFICATION"),
	);
}

/**
 * Copies the credit card CVC of a given secret to the clipboard
 *
 * @param {object} item The item of which we want to load the credit card CVC into our clipboard
 */
function copyCreditCardCvc(item: SecretReference) {
	browserClient.copyToClipboard(() =>
		readSecret(item.secret_id, item.secret_key).then(
			(decryptedSecret) => decryptedSecret["credit_card_cvc"]!,
		),
	);
	notification.push(
		"credit_card_cvc_copy",
		i18n.t("CREDIT_CARD_CVC_COPY_NOTIFICATION"),
	);
}

/**
 * Copies the credit card PIN of a given secret to the clipboard
 *
 * @param {object} item The item of which we want to load the credit card PIN into our clipboard
 */
function copyCreditCardPin(item: SecretReference) {
	browserClient.copyToClipboard(() =>
		readSecret(item.secret_id, item.secret_key).then(
			(decryptedSecret) => decryptedSecret["credit_card_pin"]!,
		),
	);
	notification.push(
		"credit_card_pin_copy",
		i18n.t("CREDIT_CARD_PIN_COPY_NOTIFICATION"),
	);
}

const secretService = {
	validPasswordModificationDatetime: validPasswordModificationDatetime,
	createSecretBulk: createSecretBulk,
	readSecretBulk: readSecretBulk,
	createSecret: createSecret,
	readSecret: readSecret,
	writeSecret: writeSecret,
	redirectSecret: redirectSecret,
	onItemClick: onItemClick,
	copyUsername: copyUsername,
	copyPassword: copyPassword,
	copyTotpToken: copyTotpToken,
	copyNoteContent: copyNoteContent,
	copyCreditCardNumber: copyCreditCardNumber,
	copyCreditCardName: copyCreditCardName,
	copyCreditCardExpiryDate: copyCreditCardExpiryDate,
	copyCreditCardCvc: copyCreditCardCvc,
	copyCreditCardPin: copyCreditCardPin,
	copyUrl: copyUrl,
};

export default secretService;
